import React from 'react';
import { Link } from 'react-router-dom';
import LegalDocumentLayout from './LegalDocumentLayout';

const PrivacyNotice = () => (
  <LegalDocumentLayout title="Privacy Notice" lastUpdated="24 June 2026">
    <p>
      TenantScore operates a property rental and short-stay
      platform in Kenya that connects landlords, Airbnb hosts, agents, and tenants. This Privacy Notice
      explains how we collect, use, share, and protect personal information when you use our website,
      mobile app, and related services.
    </p>

    <h2>1. Who this notice applies to</h2>
    <p>This notice applies to everyone who uses TenantScore, including:</p>
    <ul>
      <li>Landlords and property owners listing rental units or properties for sale</li>
      <li>Airbnb and short-stay hosts managing bookings and guest stays</li>
      <li>Tenants and guests searching for homes, submitting applications, or booking stays</li>
      <li>Agents managing properties, rent collection, and maintenance on behalf of owners</li>
      <li>Visitors browsing public listings on our website</li>
    </ul>

    <h2>2. Information we collect</h2>
    <p>Depending on how you use the Platform, we may collect:</p>
    <ul>
      <li>
        <strong>Account information:</strong> name, email address, phone number, password (stored in
        encrypted form), profile photo, and role (landlord, host, tenant, agent, or admin).
      </li>
      <li>
        <strong>Identity and verification details:</strong> information submitted during sign-up,
        OTP verification, and know-your-business (KYB) checks for business accounts.
      </li>
      <li>
        <strong>Property and listing data:</strong> addresses, photos, unit details, pricing, availability,
        amenities, and booking or rental terms you publish.
      </li>
      <li>
        <strong>TenantScore and behaviour data:</strong> payment history, rent payment evidence, lease
        adherence, property care ratings, communication records, and scores derived from platform activity.
      </li>
      <li>
        <strong>Transaction and booking data:</strong> rent amounts, due dates, booking requests, move-in
        and move-out requests, maintenance tickets, and payment confirmations.
      </li>
      <li>
        <strong>Communications:</strong> messages sent through the Platform, support enquiries, and
        notifications you receive or respond to.
      </li>
      <li>
        <strong>Technical data:</strong> device type, browser, IP address, app usage, and analytics events
        used to improve performance and security.
      </li>
    </ul>

    <h2>3. How we use your information</h2>
    <p>We use personal information to:</p>
    <ul>
      <li>Create and manage your account and authenticate your access</li>
      <li>Display and match rental, BnB, and property-for-sale listings</li>
      <li>Process tenant applications, booking requests, and lease workflows</li>
      <li>Calculate and display TenantScore ratings to help landlords and hosts make informed decisions</li>
      <li>Facilitate rent tracking, payment evidence, and financial reporting for property managers</li>
      <li>Send service notifications, OTP codes, and important account updates</li>
      <li>Detect fraud, enforce our Terms of Service, and keep the Platform secure</li>
      <li>Improve our products, fix bugs, and understand how features are used</li>
      <li>Comply with legal obligations under Kenyan law</li>
    </ul>

    <h2>4. How we share information</h2>
    <p>We do not sell your personal information. We may share data only when necessary:</p>
    <ul>
      <li>
        <strong>With other users:</strong> landlords and hosts may see tenant application details, scores,
        and relevant history; tenants may see listing and host information needed to rent or book.
      </li>
      <li>
        <strong>With agents and property managers:</strong> when you are linked to a property they manage
        on your behalf.
      </li>
      <li>
        <strong>With service providers:</strong> such as hosting, analytics, SMS/email delivery, and payment
        processing partners who help us operate the Platform under contractual safeguards.
      </li>
      <li>
        <strong>For legal reasons:</strong> when required by law, court order, or to protect the rights,
        safety, and property of TenantScore, our users, or the public.
      </li>
    </ul>

    <h2>5. Data retention</h2>
    <p>
      We keep personal information for as long as your account is active or as needed to provide services,
      resolve disputes, enforce agreements, and meet legal requirements. TenantScore history and ratings may
      be retained to support future rental decisions unless deletion is required by law or you exercise your
      rights where applicable.
    </p>

    <h2>6. Security</h2>
    <p>
      We use technical and organisational measures—including encryption in transit, access controls, and
      secure authentication—to protect your data. No online service can guarantee absolute security, but we
      work continuously to safeguard the Platform.
    </p>

    <h2>7. Your choices and rights</h2>
    <p>Depending on applicable law, including the Kenya Data Protection Act, you may have the right to:</p>
    <ul>
      <li>Access the personal information we hold about you</li>
      <li>Request correction of inaccurate information</li>
      <li>Request deletion of your account and associated data, subject to legal retention needs</li>
      <li>Object to or restrict certain processing activities</li>
      <li>Withdraw consent where processing is based on consent</li>
    </ul>
    <p>
      You can update much of your profile information in your account settings. For other requests, contact
      us using the details below.
    </p>

    <h2>8. Cookies and similar technologies</h2>
    <p>
      We use cookies and similar technologies to keep you signed in, remember preferences, and—if you
      accept non-essential cookies—measure site performance. See our <Link to="/cookies">Cookies Policy</Link>{' '}
      for details and how to change your choice. You can also control cookies through your browser settings,
      though some features may not work correctly if cookies are disabled.
    </p>

    <h2>9. Children</h2>
    <p>
      TenantScore is not intended for users under 18 years of age. We do not knowingly collect personal
      information from children.
    </p>

    <h2>10. Changes to this notice</h2>
    <p>
      We may update this Privacy Notice from time to time. When we make material changes, we will post the
      updated notice on this page and update the &quot;Last updated&quot; date. Continued use of the Platform
      after changes take effect means you accept the revised notice.
    </p>

    <h2>11. Contact us</h2>
    <p>
      If you have questions about this Privacy Notice or how we handle your data, contact TenantScore:
    </p>
    <ul>
      <li>Phone: +254 113 886 814</li>
      <li>Website: tenantscore.co.ke</li>
    </ul>
  </LegalDocumentLayout>
);

export default PrivacyNotice;
